import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePagination } from "@/hooks/usePagination";
import { START_PAGE } from "@/constants/pagination";

export type TStoredListState<T> = {
  filters: T;
  page: number;
  sorts: string | null;
};

type UseListPageStateOptions<T> = {
  storageKey: string;
  defaultFilters: T;
  basePath: string;
  legacyCheckField: keyof T;
};

type UseListPageStateReturn<T> = {
  formValues: T;
  setFormValues: React.Dispatch<React.SetStateAction<T>>;
  filters: T;
  setFilters: React.Dispatch<React.SetStateAction<T>>;
  searchParams: URLSearchParams;
  navigateFirstPage: () => void;
  onSubmit: (vals: T) => void;
  handleClearFilter: () => void;
};

export function useListPageState<T extends Record<string, unknown>>(options: UseListPageStateOptions<T>): UseListPageStateReturn<T> {
  const { storageKey, defaultFilters, basePath, legacyCheckField } = options;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pagReq = usePagination();
  const initializedRef = useRef(false);

  const getStoredState = useCallback((): TStoredListState<T> | null => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (!stored) return null;
      const parsed = JSON.parse(stored);

      if (parsed.filters && typeof parsed.filters === "object") {
        return parsed as TStoredListState<T>;
      }

      if (parsed[legacyCheckField] !== undefined) {
        return { filters: parsed as T, page: 1, sorts: null };
      }

      return null;
    } catch {
      return null;
    }
  }, [storageKey, legacyCheckField]);

  const saveStateToStorage = useCallback(
    (filters: T, page: number, sorts: string | null) => {
      sessionStorage.setItem(storageKey, JSON.stringify({ filters, page, sorts }));
    },
    [storageKey],
  );

  const getInitialState = useCallback(() => {
    const stored = getStoredState();
    if (stored) {
      return { formValues: stored.filters, filters: stored.filters };
    }
    return { formValues: defaultFilters, filters: defaultFilters };
  }, [getStoredState, defaultFilters]);

  const [formValues, setFormValues] = useState<T>(() => getInitialState().formValues);
  const [filters, setFilters] = useState<T>(() => getInitialState().filters);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const stored = getStoredState();
    if (stored) {
      const params = new URLSearchParams(searchParams);
      let needsUpdate = false;

      if (stored.page && stored.page > 1 && !searchParams.get("page")) {
        params.set("page", String(stored.page));
        needsUpdate = true;
      }
      if (stored.sorts && !searchParams.get("sorts")) {
        params.set("sorts", stored.sorts);
        needsUpdate = true;
      }

      if (needsUpdate) {
        navigate(`${basePath}?${params.toString()}`, { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    const currentPage = pagReq.page || START_PAGE;
    const currentSorts = searchParams.get("sorts");
    saveStateToStorage(filters, currentPage, currentSorts);
  }, [filters, pagReq.page, searchParams, saveStateToStorage]);

  const navigateFirstPage = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set("page", "1");
    navigate(`${basePath}?${params.toString()}`);
  }, [basePath, navigate]);

  const onSubmit = useCallback(
    (vals: T) => {
      setFilters(vals);
      navigateFirstPage();
    },
    [navigateFirstPage],
  );

  const handleClearFilter = useCallback(() => {
    setFormValues(defaultFilters);
    setFilters(defaultFilters);
    navigateFirstPage();
  }, [defaultFilters, navigateFirstPage]);

  return {
    formValues,
    setFormValues,
    filters,
    setFilters,
    searchParams,
    navigateFirstPage,
    onSubmit,
    handleClearFilter,
  };
}
